import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import { SaveIcon } from "../../common/icons";
import './ws-config.scss';
import { GET_WS_CONFIG, UPDATE_WS_CONFIG } from "../../../graphql/ws-admin-gql";
import { useLazyQuery, useMutation } from "@apollo/client";
import SpinnerOverlay from "../../common/spinner/spinner";
import { useEffect, useState } from "react";
import { IWsConfig, IWsConfigInput, IWsConfigResponse } from "../../../types/ws-admin";
import { notifySuccess } from "../../../services/notification";
import PasswordInput from "../../common/password-input";
import AccordionContainer from "../../common/accordion-container";

const initConfig = {
    maxOrgSearchResult: 0,
	maxPosSearchResult: 0,
	maxIndSearchResult: 0,
    jwtTokenExpiryTime: 0
}

const WsConfig = () => {

    const [wsConfig, setWsConfig] = useState<Partial<IWsConfigInput>>(initConfig)
    const [getWsConfig, { loading, data }] = useLazyQuery<IWsConfigResponse>(GET_WS_CONFIG, {
        onCompleted: (data) => {
            populateWsConfigInput(data?.wsConfig)
        }
    })
    const [updateWsConfig, { loading: loadingSave }] = useMutation(UPDATE_WS_CONFIG)
    const [validated, setValidated] = useState(false)

    const [jwtExpiryError, setJwtExpiryError] = useState<string>('')

    useEffect(() => {
        getWsConfig()
    }, [])

    useEffect(() => {
        validate()
    }, [wsConfig])

    const populateWsConfigInput = (wsConfig: IWsConfig) => {
        setWsConfig({
            maxOrgSearchResult: wsConfig.maxOrgSearchResult || 0,
            maxPosSearchResult: wsConfig.maxPosSearchResult || 0,
            maxIndSearchResult: wsConfig.maxIndSearchResult || 0,
            jwtTokenExpiryTime: wsConfig.jwtTokenExpiryTime || 0
        })
    }

    const validate = () => {
        let _validated = !jwtExpiryError
        setValidated(_validated)
    }

    const handleSubmit = (event: any) => {
        event.preventDefault();
        updateWsConfig({
            variables: {
                input: wsConfig
            },
            refetchQueries: [{
                query: GET_WS_CONFIG
            }],
            awaitRefetchQueries: true,
            onCompleted: (data) => {
                data && notifySuccess()
            }
        })
    }

    const onJwtExpiryChange = (event: any) => {
        let val = Number(event.target.value)
        if (val <= 0) {
            setJwtExpiryError("The value must be a positive integer")
        } else {
            setJwtExpiryError("")
        }
        setWsConfig({...wsConfig, jwtTokenExpiryTime: Number(event.target.value)})
    }

    const onMaxIndsChange = (event: any) => {
        setWsConfig({...wsConfig, maxIndSearchResult: event.target.value === '' ?  10 : Number(event.target.value)})
    }

    const onMaxOrgsChange = (event: any) => {
        setWsConfig({...wsConfig, maxOrgSearchResult: event.target.value === '' ?  10 : Number(event.target.value)})
    }

    const onMaxPossChange = (event: any) => {
        setWsConfig({...wsConfig, maxPosSearchResult: event.target.value === '' ?  10: Number(event.target.value)})
    }

    return (
        <>
        {(loading || loadingSave) && <SpinnerOverlay></SpinnerOverlay>}
        <Form id="wsConfigForm" onSubmit={handleSubmit}>
            <AccordionContainer title="API Authentication" className="pt-3">
                <Row>
                    <Col md={6}>
                        <FormGroup row>
                            <Label for="jwtExpiryTime" sm={4}>JWT expiry time (hours): </Label>
                            <Col sm={6}>
                                <Input id="jwtExpiryTime" name="jwtExpiryTime" type="number"
                                    value={wsConfig.jwtTokenExpiryTime} onChange={onJwtExpiryChange} invalid={!!jwtExpiryError}/>
                                <FormFeedback>{jwtExpiryError}</FormFeedback>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
            </AccordionContainer>

            <AccordionContainer title="Maximum Number of Results Returned" className="pt-3">
                <Row>
                    <Col md={6}>
                        <FormGroup row>
                            <Label for="maxIndIds" sm={4}>By individual search: </Label>
                            <Col sm={6}>
                                <Input id="maxIndIds" name="maxIndIds" type="number" min="10"
                                     value={wsConfig.maxIndSearchResult} onChange={onMaxIndsChange}/>
                            </Col>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup row>
                            <Label for="maxOrgIds" sm={4}>By organization search:</Label>
                            <Col sm={6}>
                                <Input id="maxOrgIds" name="maxOrgIds" type="number" min="10"
                                    value={wsConfig.maxOrgSearchResult} onChange={onMaxOrgsChange}/>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormGroup row>
                            <Label for="maxPosIds" sm={4}>By position search:</Label>
                            <Col sm={6}>
                                <Input id="maxPosIds" name="maxPosIds" type="number" min="10"
                                 value={wsConfig.maxPosSearchResult} onChange={onMaxPossChange}/>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
            </AccordionContainer>

            <AccordionContainer title="Publish Refresh Information" className="pt-3">
                <Row>
                    <Col md={6}>
                        <FormGroup row>
                            <Col sm={4}>Last OMS refresh status:</Col>
                            <Col sm={6} id="publishRefreshInfo">
                                {data?.wsConfig?.publishRefreshInfo}
                            </Col>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup row>
                            <Col sm={4}>Last OMS refresh time:</Col>
                            <Col sm={6} id="publishRefreshTime">
                                {data?.wsConfig?.publishRefreshTime}
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
            </AccordionContainer>

            <Row className="pt-3">
                <Col>
                    <Button type="submit" color="primary" disabled={!validated}>
                        <SaveIcon /> Save
                    </Button>
                </Col>
            </Row>

        </Form>
        </>
    )

}

export default WsConfig
